import { LayoutNode } from '../types';

type Token = { type: 'H' | 'V' | '[' | ']' | ',' | 'INT'; value?: number };

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const c = input[i];
    if (c === ' ' || c === '\t' || c === '\n') { i++; continue; }
    if (c === 'H') { tokens.push({ type: 'H' }); i++; continue; }
    if (c === 'V') { tokens.push({ type: 'V' }); i++; continue; }
    if (c === '[') { tokens.push({ type: '[' }); i++; continue; }
    if (c === ']') { tokens.push({ type: ']' }); i++; continue; }
    if (c === ',') { tokens.push({ type: ',' }); i++; continue; }
    if (c >= '0' && c <= '9') {
      let num = '';
      while (i < input.length && input[i] >= '0' && input[i] <= '9') {
        num += input[i];
        i++;
      }
      tokens.push({ type: 'INT', value: parseInt(num, 10) });
      continue;
    }
    throw new Error(`Unexpected character '${c}' at position ${i}`);
  }
  return tokens;
}

class Parser {
  private tokens: Token[];
  private pos = 0;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private consume(type?: Token['type']): Token {
    const tok = this.tokens[this.pos];
    if (!tok) throw new Error('Unexpected end of expression');
    if (type && tok.type !== type) throw new Error(`Expected ${type} but got ${tok.type}`);
    this.pos++;
    return tok;
  }

  parse(): LayoutNode {
    const node = this.parseExpr();
    if (this.pos < this.tokens.length) {
      throw new Error('Unexpected tokens after expression');
    }
    return node;
  }

  private parseExpr(): LayoutNode {
    const tok = this.peek();
    if (!tok) throw new Error('Unexpected end of expression');

    if (tok.type === 'INT') {
      this.consume();
      return { kind: 'leaf', index: tok.value! };
    }

    if (tok.type === 'H' || tok.type === 'V') {
      const splitType = tok.type;
      this.consume();
      this.consume('[');
      const children: LayoutNode[] = [];
      children.push(this.parseExpr());
      while (this.peek()?.type === ',') {
        this.consume(',');
        children.push(this.parseExpr());
      }
      this.consume(']');
      return {
        kind: splitType === 'H' ? 'hsplit' : 'vsplit',
        children,
      };
    }

    throw new Error(`Unexpected token ${tok.type}`);
  }
}

export function parseLayoutExpression(input: string): LayoutNode {
  const trimmed = input.trim();
  // Single integer shorthand
  if (/^\d+$/.test(trimmed)) {
    return { kind: 'leaf', index: parseInt(trimmed, 10) };
  }
  const tokens = tokenize(trimmed);
  return new Parser(tokens).parse();
}
